import { usePermissionGate } from '@/components/providers/PermissionsContextProvider/usePermissionGate';
import { Feature } from '@/components/shared/constants/features';
import { Button } from 'primereact/button';
import { useEffect, useMemo, useState } from 'react';
import { ClampedText } from '../ClampedText';
import { SuccessBanner, useSuccessBannerToast } from '../SuccessBanner';
import { SuccessBannerDescription } from '../SuccessBanner/SuccessBanner.StyledComponents';
import { TabDataState } from '../TabDataState';
import { TemplateFormDialog } from './TemplateFormDialog';
import { ActionsRow } from './TemplateManager.StyledComponents';
import { BaseTemplate, TemplateManagerConfig } from './TemplateManager.types';
import { TemplateTable } from './TemplateTable';
import { TemplateTableSkeleton } from './TemplateTableSkeleton';
import { sortTemplates } from './sortTemplates';

const MANAGE_DENIED_MESSAGE = "You don't have permission to manage templates.";
const HIGHLIGHT_DURATION_MS = 5000;

export const TemplateManager = <T extends BaseTemplate, TExtra extends object = Record<never, never>>({
    title,
    items,
    placeholders,
    isLoading,
    isLoadingError,
    isRefetching,
    onRetry,
    onCreate,
    onUpdate,
    onRetireItem,
    onReactivateItem,
    onPreview,
    canManage,
    canEdit,
    extraBadge,
    extraColumns,
    sortGroup,
    canRetire,
    extraFields,
}: TemplateManagerConfig<T, TExtra>) => {
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<T | null>(null);
    const [highlightedId, setHighlightedId] = useState<string | null>(null);
    const { banner, showBanner } = useSuccessBannerToast(
        'template-manager-banners',
        'template-created-banner',
    );

    useEffect(() => {
        if (highlightedId == null) {
            return undefined;
        }
        const timer = setTimeout(() => setHighlightedId(null), HIGHLIGHT_DURATION_MS);
        return () => clearTimeout(timer);
    }, [highlightedId]);

    const sortedItems = useMemo(() => sortTemplates(items, sortGroup), [items, sortGroup]);

    const addNewGate = usePermissionGate(Feature.ManageTemplates, MANAGE_DENIED_MESSAGE, canManage);

    const openCreate = () => {
        setEditingItem(null);
        setIsFormOpen(true);
    };

    const openEdit = (item: T) => {
        setEditingItem(item);
        setIsFormOpen(true);
    };

    const handleSaved = (saved: T) => {
        setIsFormOpen(false);
        if (editingItem === null) {
            showBanner((dismiss) => (
                <SuccessBanner
                    testIdPrefix="template-created-banner"
                    title={`✓ ${title} Created:`}
                    onDismiss={dismiss}
                    onViewNow={() => {
                        dismiss();
                        setHighlightedId(saved.id);
                    }}
                >
                    <SuccessBannerDescription>
                        <ClampedText expanded={false} lines={3}>
                            {saved.label}
                        </ClampedText>
                    </SuccessBannerDescription>
                </SuccessBanner>
            ));
        }
    };

    return (
        <div data-testid="template-manager">
            {banner}
            <ActionsRow>
                <Button
                    label={`+ New ${title}`}
                    data-testid="template-manager-add-new"
                    aria-disabled={addNewGate.disabled || undefined}
                    title={addNewGate.title}
                    onClick={addNewGate.gateAction(openCreate)}
                />
            </ActionsRow>
            <TabDataState
                isLoading={isLoading}
                isLoadingError={isLoadingError}
                isRefetching={isRefetching}
                onRetry={onRetry}
                isEmpty={items.length === 0}
                emptyMessage={`No ${title}s yet.`}
                errorMessage={`Failed to load ${title}s.`}
                testId="template-manager-list"
                skeleton={<TemplateTableSkeleton extraColumns={extraColumns} />}
            >
                <TemplateTable
                    items={sortedItems}
                    canManage={canManage}
                    canEdit={canEdit}
                    extraBadge={extraBadge}
                    extraColumns={extraColumns}
                    canRetire={canRetire}
                    highlightedId={highlightedId}
                    onEdit={openEdit}
                    onRetire={onRetireItem}
                    onReactivate={onReactivateItem}
                />
            </TabDataState>
            <TemplateFormDialog
                visible={isFormOpen}
                onHide={() => setIsFormOpen(false)}
                editingItem={editingItem}
                placeholders={placeholders(editingItem)}
                onPreview={onPreview}
                onCreate={onCreate}
                onUpdate={onUpdate}
                onSaved={handleSaved}
                extraFields={extraFields}
            />
        </div>
    );
};
